import { useEffect, useRef, useState } from 'react';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { MEDIA, useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cx } from '@/shared/ui/cx';
import { safeErrorMessage } from '@/shared/utils/errors';
import {
  getStatusSummary,
  type StatusDay,
  type StatusLevel,
  type StatusSummary,
} from '@/features/status/services/statusService';

/*
 * How much history the strip shows, and why that is two numbers rather than one.
 *
 * 90 days is the status-page convention and the right default on a laptop. It
 * does not survive a phone. `.status-daybar-row` is a flex row with a 2px gap
 * and each `.status-daybar` has a 2px floor, so 90 days asserts a minimum of
 * 90*2 + 89*2 = 358px inside the ~324px a 375px viewport leaves after the page
 * gutters. That is 18px of real document overflow — measured on the page, not
 * inferred — and it is why /status scrolls sideways on an iPhone.
 *
 * The fix is fewer bars, not thinner ones. Dropping the floor to 1px makes the
 * arithmetic work and conveys nothing: 90 hairlines is a green smear, and a
 * green smear is zero days of history however neatly it fits. 30 bars at a 3px
 * floor with 1px gaps needs 119px, leaves each bar around 10px wide, and is a
 * strip somebody can read — and, since every bar is now a button, hit.
 *
 * The trade is real and worth saying out loud: a phone shows a month of history
 * where a laptop shows a quarter. The caption reports `data.days` — what the
 * server actually answered — so the page says which of the two you are looking
 * at instead of claiming 90 in both cases, which is what it used to do.
 */
const DAYS_WIDE = 90;
const DAYS_NARROW = 30;
const REFRESH_INTERVAL_MS = 60_000;

const BANNER_COPY: Record<StatusLevel, string> = {
  operational: 'All Systems Operational',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial System Outage',
  major_outage: 'Major System Outage',
};

const SERVICE_LABEL: Record<StatusLevel, string> = {
  operational: 'Operational',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial Outage',
  major_outage: 'Major Outage',
};

const INCIDENT_ICON: Record<StatusLevel, string> = {
  major_outage: '✕',
  partial_outage: '⚠',
  degraded: '⚠',
  operational: '✓',
};

function formatDayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// How many of this day's 24 hours were AT the day's own worst level — computed
// from the same success/failure data as `day.status` (the worst of these same 24
// hours, see daily_service_history's docstring). Counting every non-operational
// hour here (degraded AND partial_outage AND major_outage) while labeling with
// only the worst of the three would overstate that label's duration — a day with
// 2 degraded hours + 1 major-outage hour would report "3 hrs Major Outage" when
// only 1 of those hours actually was. Matching the count to the label's own level
// keeps the two honest together.
function hoursAtLevel(hourly: StatusLevel[], level: StatusLevel): number {
  return hourly.filter((h) => h === level).length;
}

function DayPopover({
  day,
  index,
  total,
  anchored,
}: {
  day: StatusDay;
  index: number;
  total: number;
  anchored: boolean;
}) {
  const hours = hoursAtLevel(day.hourly, day.status);

  /* Anchored to its own bar on a wide screen; a full-width row under the strip
     on a phone, where a 200px panel centred on one of thirty bars has nowhere to
     sit that is not off an edge. The narrow case drops the inline `left`
     entirely rather than letting the stylesheet argue with it — an inline
     declaration can only be beaten with `!important`, and this component is the
     one writing the value, so the honest place to not write it is here. */
  return (
    <div
      className="status-popover"
      style={anchored ? { left: `${((index + 0.5) / total) * 100}%` } : undefined}
      role="tooltip"
    >
      <div className="status-popover__date">{formatDayLabel(day.date)}</div>
      {day.status === 'operational' ? (
        <div className={cx('status-popover__row', 'status-popover__row--operational')}>
          <span className="status-popover__icon">{INCIDENT_ICON.operational}</span>
          <span className="status-popover__label">No incidents reported</span>
        </div>
      ) : (
        <div className={cx('status-popover__row', `status-popover__row--${day.status}`)}>
          <span className="status-popover__icon">{INCIDENT_ICON[day.status]}</span>
          <span className="status-popover__label">{SERVICE_LABEL[day.status]}</span>
          <span className="status-popover__duration">
            {hours} hr{hours === 1 ? '' : 's'}
          </span>
        </div>
      )}
    </div>
  );
}

function ServiceRow({
  name,
  status,
  uptimePct,
  daily,
  days,
  anchoredPopover,
}: {
  name: string;
  status: StatusLevel;
  uptimePct: number | null;
  daily: StatusDay[];
  days: number;
  anchoredPopover: boolean;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  /* Which bar was open when the current press started, captured on
     `pointerdown`. It exists because a tap is not a click on its own: the
     browser synthesises the whole mouse sequence after `touchend`, so a finger
     landing on a bar fires `mouseenter` — which opens the popover — and THEN
     `click`. A click handler that toggled `openIndex` directly would therefore
     close, on the same tap, the popover the enter had just opened: the panel
     flashes, vanishes, and touch still has no route to the data. `pointerdown`
     runs before the synthetic events, so this holds the state from before the
     gesture and the toggle is computed against that — first tap opens and stays
     open, second tap on the same bar closes. A real mouse click still toggles,
     which is the dismissal a pointer user would expect from clicking a tooltip
     away. */
  const openAtPressStart = useRef<number | null>(null);

  const open = (index: number) => setOpenIndex(index);
  const close = (index: number) => setOpenIndex((current) => (current === index ? null : current));

  return (
    <div className="status-service">
      <div className="status-service__head">
        <span className="status-service__name">{name}</span>
        <span className={cx('status-service__label', `status-service__label--${status}`)}>
          {SERVICE_LABEL[status]}
        </span>
      </div>
      {/* Escape closes the open day without moving focus off the bar, which is
          the dismissal WCAG asks of anything shown on hover or focus. One
          handler on the row rather than one per bar: the event bubbles up from
          whichever button has focus, and there are up to ninety of them. */}
      <div
        className="status-daybar-row"
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpenIndex(null);
        }}
      >
        {/* A button, and not a div carrying `onMouseEnter`, which is what this
            was. The day's status and duration lived behind a pointer hover on a
            non-focusable element: no route by touch, none by keyboard, none by
            screen reader — on the one page whose entire job is to be legible
            during an outage. `aria-label` carries the whole answer, so assistive
            tech never needs the popover to have been opened, and the strip does
            not need ninety nodes of visually-hidden text to say it. */}
        {daily.map((d, i) => (
          <button
            key={d.date}
            type="button"
            className={cx('status-daybar', `status-daybar--${d.status}`)}
            aria-label={`${formatDayLabel(d.date)} — ${SERVICE_LABEL[d.status]}`}
            onMouseEnter={() => open(i)}
            onMouseLeave={() => close(i)}
            onFocus={() => open(i)}
            onBlur={() => close(i)}
            onPointerDown={() => {
              openAtPressStart.current = openIndex;
            }}
            onClick={() => {
              const wasOpen = openAtPressStart.current === i;
              openAtPressStart.current = null;
              setOpenIndex(wasOpen ? null : i);
            }}
          />
        ))}
        {openIndex !== null && (
          <DayPopover
            day={daily[openIndex]}
            index={openIndex}
            total={daily.length}
            anchored={anchoredPopover}
          />
        )}
      </div>
      <div className="status-service__caption">
        <span>{days} days ago</span>
        <span className="status-service__caption-rule" />
        <span>{uptimePct === null ? 'No data' : `${uptimePct.toFixed(2)} % uptime`}</span>
        <span className="status-service__caption-rule" />
        <span>Today</span>
      </div>
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState<StatusSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const narrow = useMediaQuery(MEDIA.phone);
  const days = narrow ? DAYS_NARROW : DAYS_WIDE;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const summary = await getStatusSummary(days);
        if (cancelled) return;
        setData(summary);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(safeErrorMessage(err, 'Unable to load status right now.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    /* `days` is a dependency, so crossing 640px refetches. Without it a phone
       rotated into landscape would keep rendering the 30 bars it fetched while
       the layout had gone back to sizing for 90 — and a desktop window dragged
       narrow would keep all 90, which is the overflow this whole change exists
       to remove. The stale set is not visibly wrong, which is exactly why it
       would have survived review: the caption would read "30 days ago" beside a
       strip laid out for a quarter. `setLoading(true)` does not blank the page
       on a refetch — the spinner branch also needs `!data` — so the strip is
       swapped in place rather than flashing. */
  }, [days]);

  return (
    <div className="status-page">
      <div className="status-page__inner">
        <header className="status-page__header">
          <span className="status-page__wordmark">Valoi Status</span>
        </header>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {loading && !data ? (
          <div className="loading-container">
            <div className="loading-spinner" />
          </div>
        ) : (
          data && (
            <>
              <div className={cx('status-banner', `status-banner--${data.overallStatus}`)}>
                {BANNER_COPY[data.overallStatus]}
              </div>

              <p className="status-page__subhead">Uptime over the past {data.days} days.</p>

              <div className="status-service-list">
                {data.services.map((svc) => (
                  <ServiceRow
                    key={svc.name}
                    name={svc.name}
                    status={svc.status}
                    uptimePct={svc.uptimePct}
                    daily={svc.daily}
                    /* `data.days`, not the `days` this component asked for. The
                       caption used to render a module constant while the subhead
                       three lines up rendered the response, so the two could
                       disagree about the same period — and now that the request
                       depends on the viewport, they would. */
                    days={data.days}
                    anchoredPopover={!narrow}
                  />
                ))}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
